import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from './useAuthStore.js'
import { S } from '../locales/strings.js'

export interface ChatMessage {
  fromName: string
  fromId: string
  text: string
  ts: number
}

interface ChatOverlayProps {
  sendMessage: (text: string) => void
  messages: ChatMessage[]
}

const QUICK_PHRASES = ['GG!', 'Nice move!', 'Good luck!', 'Gg wp', '🔥'] as const

export function ChatOverlay({ sendMessage, messages }: ChatOverlayProps): React.ReactElement {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [input, setInput] = React.useState('')
  const [lastSent, setLastSent] = React.useState(0)
  const [unread, setUnread] = React.useState(0)
  const listRef = React.useRef<HTMLDivElement>(null)
  const session = useAuthStore((s) => s.session)
  const myId = session?.userId ?? ''

  // Track unread count when panel is closed
  const prevLenRef = React.useRef(0)
  React.useEffect(() => {
    if (!open && messages.length > prevLenRef.current) {
      setUnread(u => u + (messages.length - prevLenRef.current))
    }
    prevLenRef.current = messages.length
  }, [messages.length, open])

  // Clear unread when opening
  const handleToggle = () => {
    setOpen(o => {
      if (!o) setUnread(0)
      return !o
    })
  }

  // Auto-scroll to bottom when panel is open
  React.useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [messages, open])

  const send = (text: string) => {
    const now = Date.now()
    const trimmed = text.trim().slice(0, 80)
    if (!trimmed || now - lastSent < 2000) return
    sendMessage(trimmed)
    setLastSent(now)
    setInput('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      send(input)
    }
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: 80,
      right: 12,
      zIndex: 150,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 4,
      pointerEvents: 'auto',
    }}>
      {/* Expanded chat panel */}
      {open && (
        <div style={{
          width: 240,
          background: 'rgba(8,6,15,0.92)',
          border: '1px solid #333',
          borderRadius: 10,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.7)',
        }}>
          {/* Message list */}
          <div
            ref={listRef}
            style={{
              maxHeight: 140,
              overflowY: 'auto',
              padding: '6px 8px',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {messages.length === 0 ? (
              <div style={{ color: '#555', fontSize: 11, textAlign: 'center', padding: '8px 0' }}>
                {t(S.CHAT_NO_MESSAGES)}
              </div>
            ) : (
              messages.slice(-20).map((m, i) => (
                <div
                  key={`${m.ts}-${i}`}
                  style={{
                    fontSize: 11,
                    textAlign: m.fromId === myId ? 'right' : 'left',
                  }}
                >
                  <span style={{ color: '#666', fontSize: 10 }}>{m.fromName}: </span>
                  <span style={{ color: m.fromId === myId ? '#C9A84C' : '#ddd' }}>{m.text}</span>
                </div>
              ))
            )}
          </div>

          {/* Quick phrases */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            padding: '4px 6px',
            borderTop: '1px solid #222',
          }}>
            {QUICK_PHRASES.map(p => (
              <button
                key={p}
                onClick={() => send(p)}
                aria-label={`Schnellnachricht: ${p}`}
                style={{
                  background: '#1a1a2e',
                  color: '#C9A84C',
                  border: '1px solid #333',
                  borderRadius: 4,
                  padding: '2px 6px',
                  fontSize: 10,
                  cursor: 'pointer',
                  minHeight: 24,
                  minWidth: 44,
                }}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Text input */}
          <div style={{
            display: 'flex',
            gap: 4,
            padding: '4px 6px',
            borderTop: '1px solid #222',
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={80}
              placeholder={t(S.CHAT_PLACEHOLDER)}
              aria-label={t(S.CHAT_PLACEHOLDER)}
              style={{
                flex: 1,
                background: '#0a0814',
                color: '#fff',
                border: '1px solid #333',
                borderRadius: 4,
                padding: '4px 6px',
                fontSize: 11,
                minHeight: 28,
                outline: 'none',
              }}
            />
            <button
              onClick={() => send(input)}
              aria-label={t(S.CHAT_SEND)}
              style={{
                background: '#C9A84C',
                color: '#000',
                border: 'none',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 11,
                cursor: 'pointer',
                minHeight: 28,
                minWidth: 44,
                fontWeight: 700,
              }}
            >
              ▶
            </button>
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={handleToggle}
        aria-label={open ? 'Chat schließen' : t(S.CHAT_TOGGLE)}
        aria-expanded={open}
        style={{
          background: open ? '#C9A84C' : 'rgba(8,6,15,0.85)',
          color: open ? '#000' : '#C9A84C',
          border: '1px solid #C9A84C',
          borderRadius: 20,
          padding: '6px 12px',
          fontSize: 13,
          cursor: 'pointer',
          minHeight: 36,
          minWidth: 44,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontFamily: 'monospace',
        }}
      >
        <span>💬</span>
        {!open && unread > 0 && (
          <span style={{
            background: '#e53935',
            color: '#fff',
            borderRadius: 10,
            padding: '0 5px',
            fontSize: 10,
            fontWeight: 700,
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
    </div>
  )
}
